"use client";

import { usePriceFormatter } from "@/hooks/usePriceFormatter";
import clsx from "clsx";
import Link from "next/link";

/**
 * DynamicStats
 *
 * Props:
 * - stats: Gösterilecek istatistik öğeleri dizisi
 *   [{ title, value, isPrice, currency, redirectUrl }]
 * - containerClass: Dış kart kabuğu için class (varsayılan: bg-background-global rounded-lg)
 * - gridClass: Izgara yapısını dışarıdan tamamen override etmek için class (boş ise otomatik düzen kullanılır)
 * - statItemClass: Her bir stat öğesi için kapsayıcı class (varsayılan: "space-y-1 p-6 text-center lg:px-6 lg:py-10")
 */
export default function DynamicStats({
  stats,
  containerClass = "bg-background-global rounded-lg",
  gridClass = "",
  statItemClass = "space-y-1 p-6 text-center lg:px-6 lg:py-10",
}) {
  const { formatPrice } = usePriceFormatter();
  const statsCount = stats?.length || 0;

  // fiyat formatlama usePriceFormatter ile sağlanır

  // Responsive grid
  const getResponsiveClasses = (count) => {
    const baseClasses = "grid divide-border-card";

    switch (count) {
      case 2:
        return `${baseClasses} grid-cols-1 md:grid-cols-2 
        divide-y md:divide-x md:divide-y-0`;

      case 4:
        return `${baseClasses} grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 
        divide-y md:divide-x-0 md:divide-y-0 lg:divide-x lg:divide-y-0 
        md:[&>*:nth-child(-n+2)]:border-b lg:[&>*:nth-child(-n+2)]:border-b-0`;

      case 3:
        return `${baseClasses} grid-cols-1 md:grid-cols-2 lg:grid-cols-3 
        divide-y md:divide-x-0 md:divide-y-0 lg:divide-x lg:divide-y-0 
        md:[&>*:nth-child(-n+2)]:border-b lg:[&>*:nth-child(-n+2)]:border-b-0`;

      case 5:
        return `${baseClasses} grid-cols-2 md:grid-cols-6 lg:grid-cols-5 xl:grid-cols-5 
        lg:[&>*]:!col-span-1 xl:[&>*]:!col-span-1 
        divide-y md:divide-y-0 lg:divide-y-0`;

      default:
        return `${baseClasses} grid-cols-1 md:grid-cols-2 lg:grid-cols-3 
        divide-y md:divide-x-0 md:divide-y-0 lg:divide-x lg:divide-y-0 
        md:[&>*:nth-child(-n+2)]:border-b lg:[&>*:nth-child(-n+2)]:border-b-0`;
    }
  };

  // Tablet düzeni (3 ve 5 için özel)
  const getTabletLayoutClasses = (count) => {
    if (count === 3) {
      return `
        md:[&>*:nth-child(3)]:col-span-2 
        md:[&>*:nth-child(3)]:text-center 
        lg:[&>*:nth-child(3)]:col-span-1
      `;
    }
    if (count === 5) {
      return `
        md:[&>*:nth-child(1)]:col-span-2 
        md:[&>*:nth-child(2)]:col-span-2 
        md:[&>*:nth-child(3)]:col-span-2 
        md:[&>*:nth-child(4)]:col-span-3 
        md:[&>*:nth-child(5)]:col-span-3 
        md:[&>*:nth-child(4)]:border-r 
        
        /* Desktop reset */
        lg:[&>*]:col-span-1 
        lg:[&>*:nth-child(4)]:border-r-0
      `;
    }
    return "";
  };

  // Mobile düzeni (5 için özel 2+2+1)
  const getMobileLayoutClasses = (count) => {
    if (count === 5) {
      return `
        [&>*:nth-child(5)]:col-span-2 
        [&>*:nth-child(3)]:border-t 
        [&>*:nth-child(4)]:border-t 

        /* Desktop reset */
        lg:[&>*:nth-child(5)]:col-span-1 
        lg:[&>*:nth-child(3)]:border-t-0 
        lg:[&>*:nth-child(4)]:border-t-0
      `;
    }
    return "";
  };

  if (!stats || stats.length === 0) {
    return null;
  }

  return (
    <div className={clsx(containerClass)}>
      <div className={clsx(gridClass || clsx(getResponsiveClasses(statsCount), getTabletLayoutClasses(statsCount), getMobileLayoutClasses(statsCount)))}>
        {stats.map((stat, index) => (
          <Stat
            key={index}
            title={stat.title}
            value={stat.value}
            isPrice={stat.isPrice}
            currency={stat.currency}
            formatPrice={formatPrice}
            itemClass={statItemClass}
            redirectUrl={stat.redirectUrl}
          />
        ))}
      </div>
    </div>
  );
}

function Stat({
  title,
  value,
  isPrice = false,
  currency,
  formatPrice,
  itemClass = "space-y-1 p-6 text-center lg:px-6 lg:py-10",
  redirectUrl = null,
}) {
  const displayValue = isPrice ? formatPrice(value, currency) : value;

  const content = (
    <div className={itemClass}>
      <p className="text-lg font-normal text-black">{title}</p>
      <p className="text-lg font-semibold text-black">{displayValue}</p>
    </div>
  );

  if (!redirectUrl) {
    return content;
  }

  return (
    <Link
      title={title || ""}
      href={redirectUrl}
      className="block cursor-pointer transition-colors hover:bg-gray-50"
    >
      {content}
    </Link>
  );
}
